import {
	ApplicationConfig,
	ErrorHandler,
	importProvidersFrom,
	inject,
	provideAppInitializer,
	provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, RouteReuseStrategy, Router, withHashLocation } from '@angular/router';
import { appRoutes } from './app.routes';
import {
	HttpClient,
	provideHttpClient,
	withInterceptors,
	withInterceptorsFromDi,
	withXhr,
} from '@angular/common/http';
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import {
	AGILOX_API_ENDPOINT,
	App,
	APP_NAME,
	AppNavMenuPointsService,
	CachedRouteReuseStrategy,
	ChunkLoadingFailedErrorHandler,
	ENVIRONMENT,
	headersInterceptor,
	initializeTranslations,
	localeProvider,
	MENU_POINTS,
	MenuPointService,
	PLAUSIBLE_DOMAIN,
	PlausibleService,
	provideDatepickerTranslationsWithNgxTranslate,
	provideSelectTranslationsWithNgxTranslate,
	provideTableSort,
	ThemeService,
	TranslationsLoader,
	USER_FORMAT,
	provideFavicon,
} from '@agilox/common';
import * as Sentry from '@sentry/angular';
import { environment } from '@plan/env/environment';
import {
	AUTH_CONFIG,
	AuthModule,
	AuthService,
	MSALAUTHConfigFactory,
	userInitializationFactory,
} from '@auth';
import { provideUserContext, UserStoreService } from '@user-store';
import { createErrorHandlerInterceptor } from '@agilox/interceptors';
import { mobileMenuPoints } from './general/shared/models/consts/mobile-menupoints';
import { desktopMenuPoints } from './general/shared/models/consts/desktop-menupoints';
import { provideAnimations } from '@angular/platform-browser/animations';
import { DatePipe, DecimalPipe } from '@angular/common';
import { CustomDatePipe } from '@agilox/pipe/custom-date';
import { CustomTimePipe } from '@agilox/pipe/custom-time';
import { CustomNumberPipe, provideNumberFormatter } from '@agilox/pipe/custom-number';
import { ToastService, UserNotificationsService, VehiclesSelectService } from '@agilox/components';

export const appConfig: ApplicationConfig = {
	providers: [
		provideFavicon(),
		importProvidersFrom(AuthModule),
		UserStoreService,
		...provideUserContext(),
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideRouter(appRoutes, withHashLocation()),
		{ provide: RouteReuseStrategy, useClass: CachedRouteReuseStrategy },
		provideHttpClient(
			withXhr(),
			withInterceptors([
				createErrorHandlerInterceptor({ app: App.plan, environment, onError: 'propagate' }),
				headersInterceptor,
			]),
			withInterceptorsFromDi() // needed for MSAL
		),
		provideAnimations(),
		provideSelectTranslationsWithNgxTranslate(),
		provideTableSort(),
		provideDatepickerTranslationsWithNgxTranslate(),
		{
			provide: ErrorHandler,
			useValue: Sentry.createErrorHandler({
				showDialog: false,
			}),
		},
		{
			provide: Sentry.TraceService,
			deps: [Router],
		},
		{
			provide: ErrorHandler,
			useClass: ChunkLoadingFailedErrorHandler,
		},
		provideAppInitializer(() => {
			const initializerFn = () => {
				const traceService = inject(Sentry.TraceService);
				return () => {};
			};
			return Promise.resolve(initializerFn());
		}),
		provideTranslateService({
			loader: {
				provide: TranslateLoader,
				useClass: TranslationsLoader,
				deps: [HttpClient],
			},
		}),
		{
			provide: MENU_POINTS,
			useValue: {
				desktop: desktopMenuPoints,
				mobile: mobileMenuPoints,
			},
		},
		MenuPointService,
		provideAppInitializer(() => {
			const authService = inject(AuthService);
			const userStore = inject(UserStoreService);
			return userInitializationFactory(authService.msalInstance, userStore)();
		}),
		provideAppInitializer(() => {
			inject(ThemeService);
		}),
		AppNavMenuPointsService,
		{
			provide: USER_FORMAT,
			useFactory: (userStore: UserStoreService) => userStore.userFormat,
			deps: [UserStoreService],
		},
		{
			provide: AUTH_CONFIG,
			useFactory: MSALAUTHConfigFactory,
			deps: [ENVIRONMENT],
		},
		TranslateService,
		provideAppInitializer(() => {
			const initializerFn = initializeTranslations(inject(TranslateService));
			return initializerFn();
		}),
		{
			provide: ENVIRONMENT,
			useValue: environment,
		},
		{
			provide: APP_NAME,
			useValue: App.plan,
		},
		{
			provide: AGILOX_API_ENDPOINT,
			useValue: environment.server,
		},
		{
			provide: UserNotificationsService,
			deps: [AGILOX_API_ENDPOINT],
		},
		{
			provide: PLAUSIBLE_DOMAIN,
			useValue: 'plan.agilox.net',
		},
		PlausibleService,
		provideAppInitializer(() => {
			inject(PlausibleService);
		}),
		localeProvider,
		DatePipe,
		DecimalPipe,
		CustomDatePipe,
		CustomTimePipe,
		CustomNumberPipe,
		...provideNumberFormatter(),
		ToastService,
		VehiclesSelectService,
	],
};
